import React, { useState } from 'react';
import { useShop } from '../context/ShopContext';
import { formatINR } from '../data/products';
import { 
  Calculator, 
  Sparkles, 
  ShieldCheck, 
  MessageCircle, 
  Printer, 
  RotateCcw, 
  Percent, 
  Gem, 
  Scale, 
  CheckCircle2,
  Info,
  HelpCircle
} from 'lucide-react';

export default function JewelleryPriceCalculator() {
  const { 
    masterMetalRates, 
    liveGoldRates, 
    storeSettings, 
    rateApiSettings, 
    marketingOfferData,
    addToast 
  } = useShop();

  // Selected Metal
  const [metalPurity, setMetalPurity] = useState('gold22K'); // 'gold24K', 'gold22K', 'gold18K', 'silver999'

  // Dynamic rates
  const rate24K = masterMetalRates?.gold?.find((g) => g.label?.includes('24') || g.id?.includes('24'))?.rate || liveGoldRates?.gold24K || 7380;
  const rate22K = masterMetalRates?.gold?.find((g) => g.label?.includes('22') || g.id?.includes('22'))?.rate || liveGoldRates?.gold22K || 6765;
  const rate18K = masterMetalRates?.gold?.find((g) => g.label?.includes('18') || g.id?.includes('18'))?.rate || liveGoldRates?.gold18K || 5535;
  const rateSilver = masterMetalRates?.silver?.find((s) => s.label?.includes('999') || s.id?.includes('999'))?.rate || liveGoldRates?.silver999 || 89.5;

  const getCurrentRate = () => {
    switch (metalPurity) {
      case 'gold24K': return rate24K;
      case 'gold22K': return rate22K;
      case 'gold18K': return rate18K;
      case 'silver999': return rateSilver;
      default: return rate22K;
    }
  };

  const getMetalTitle = () => {
    switch (metalPurity) {
      case 'gold24K': return '24K Gold (999 Pure Bullion)';
      case 'gold22K': return '22K Gold (916 BIS Hallmarked)';
      case 'gold18K': return '18K Gold (750 Diamond Jewellery)';
      case 'silver999': return '999 Pure Silver';
      default: return '22K Gold (916)';
    }
  };

  const currentRate = getCurrentRate();

  // Inputs
  const [weight, setWeight] = useState(8.5);
  const [makingChargeType, setMakingChargeType] = useState('percent'); // 'percent' or 'perGram'
  const [makingChargeValue, setMakingChargeValue] = useState(rateApiSettings?.defaultMakingCharge || 6.99);
  const [wastagePercent, setWastagePercent] = useState(0);
  const [stoneCharges, setStoneCharges] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(rateApiSettings?.makingDiscountPercent || 50); // Discount on making charge
  const [discountType, setDiscountType] = useState('onMaking'); // 'onMaking' or 'onTotal'

  // Calculations
  const parsedWeight = Math.max(0.1, parseFloat(weight) || 0);
  const metalValue = Math.round(parsedWeight * currentRate);

  let rawMakingCharge = 0;
  if (makingChargeType === 'percent') {
    rawMakingCharge = Math.round(metalValue * ((parseFloat(makingChargeValue) || 0) / 100));
  } else {
    rawMakingCharge = Math.round(parsedWeight * (parseFloat(makingChargeValue) || 0));
  }

  const wastageCharges = Math.round(metalValue * ((parseFloat(wastagePercent) || 0) / 100));
  const parsedStoneCharges = Math.max(0, parseFloat(stoneCharges) || 0);

  // Discount calculation
  let discountAmount = 0;
  const parsedDiscountPercent = Math.max(0, parseFloat(discountPercent) || 0);
  if (discountType === 'onMaking') {
    discountAmount = Math.round(rawMakingCharge * (parsedDiscountPercent / 100));
  } else {
    const grossVal = metalValue + rawMakingCharge + wastageCharges + parsedStoneCharges;
    discountAmount = Math.round(grossVal * (parsedDiscountPercent / 100));
  }

  const effectiveMakingCharge = Math.max(0, rawMakingCharge - (discountType === 'onMaking' ? discountAmount : 0));
  const taxableAmount = Math.max(0, metalValue + effectiveMakingCharge + wastageCharges + parsedStoneCharges - (discountType === 'onTotal' ? discountAmount : 0));
  const gstAmount = Math.round(taxableAmount * 0.03); // 3% Indian GST on bullion and precious jewellery
  const finalPrice = taxableAmount + gstAmount;

  // Reset Form
  const handleReset = () => {
    setWeight(8.5);
    setMakingChargeType('percent');
    setMakingChargeValue(rateApiSettings?.defaultMakingCharge || 6.99);
    setWastagePercent(0);
    setStoneCharges(0);
    setDiscountPercent(rateApiSettings?.makingDiscountPercent || 50);
    addToast('Calculator reset to showroom standard defaults', 'info');
  };

  // WhatsApp Share Quote
  const handleShareWhatsApp = () => {
    const phone = marketingOfferData?.whatsappNumber || storeSettings?.storePhone || '+919810023456';
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const message = encodeURIComponent(
      `*M.L. JEWELLERS — INSTANT PRICE ESTIMATE*\n\n` +
      `Metal: ${getMetalTitle()}\n` +
      `Live Bullion Rate: ₹${currentRate}/g\n` +
      `Net Weight: ${parsedWeight}g\n` +
      `--------------------------------\n` +
      `Metal Value: ${formatINR(metalValue)}\n` +
      `Making Charges: ${formatINR(rawMakingCharge)} (${makingChargeValue}${makingChargeType === 'percent' ? '%' : '₹/g'})\n` +
      (wastageCharges > 0 ? `Wastage Charges: ${formatINR(wastageCharges)} (${wastagePercent}%)\n` : '') +
      (parsedStoneCharges > 0 ? `Stone/Diamond Charges: ${formatINR(parsedStoneCharges)}\n` : '') +
      (discountAmount > 0 ? `Special Discount: -${formatINR(discountAmount)} (${discountPercent}% OFF)\n` : '') +
      `Applicable GST (3%): ${formatINR(gstAmount)}\n` +
      `================================\n` +
      `*FINAL ESTIMATED PRICE: ${formatINR(finalPrice)}*\n` +
      `================================\n` +
      `Generated via Live Bullion Portal • BIS 916 Hallmarked`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  // Print Quote
  const handlePrint = () => {
    window.print();
  };

  return (
    <section 
      id="jewellery-calculator-section"
      className="jewellery-calculator-section" 
      style={{
        padding: '2rem 1rem 2.5rem',
        background: '#ffffff',
        borderBottom: '1px solid #e0e0e0'
      }}
    >
      <div className="container" style={{ maxWidth: '1240px', margin: '0 auto' }}>
        
        {/* Section Heading */}
        <div style={{ textAlign: 'center', marginBottom: '1.8rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            background: '#fff8e1',
            border: '1px solid #ffe082',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.74rem',
            fontWeight: 800,
            color: '#b78103',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.4rem'
          }}>
            <Calculator size={13} />
            <span>Transparent Jewellery Billing Engine</span>
          </div>
          <h2 style={{
            margin: '0 0 0.4rem',
            fontSize: 'clamp(1.3rem, 2.4vw, 1.85rem)',
            fontWeight: 800,
            color: '#212121',
            fontFamily: "'Playfair Display', serif, Georgia"
          }}>
            Live Jewellery Price Calculator
          </h2>
          <p style={{
            margin: 0,
            fontSize: '0.86rem',
            color: '#666',
            maxWidth: '620px',
            margin: '0 auto'
          }}>
            Calculate the exact showroom price for any ornament with live bullion rates, transparent making charges, and official 3% GST.
          </p>
        </div>

        {/* 2-Column Responsive Card: Left Form Inputs, Right Live Breakdown */}
        <div 
          className="jewel-calc-card"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            background: '#f8fafc',
            border: '1.5px solid #e2e8f0',
            borderRadius: '10px',
            padding: '1.5rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)'
          }}
        >

          {/* LEFT COLUMN: User Inputs */}
          <div>
            <h3 style={{
              margin: '0 0 1rem',
              fontSize: '1rem',
              fontWeight: 800,
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}>
              <span>1. Select Metal & Enter Specifications</span>
            </h3>

            {/* Metal Selector Chips */}
            <div style={{ marginBottom: '1.1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '0.4rem' }}>
                Precious Metal & Carat Purity:
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                {[
                  { id: 'gold24K', label: '24K Gold (999)', rate: rate24K },
                  { id: 'gold22K', label: '22K Gold (916)', rate: rate22K, badge: 'Popular' },
                  { id: 'gold18K', label: '18K Gold (750)', rate: rate18K },
                  { id: 'silver999', label: 'Silver 999', rate: rateSilver }
                ].map((item) => {
                  const isSelected = metalPurity === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setMetalPurity(item.id)}
                      style={{
                        padding: '0.65rem 0.8rem',
                        borderRadius: '6px',
                        border: isSelected ? '2px solid #2874f0' : '1px solid #cbd5e1',
                        background: isSelected ? '#eff6ff' : '#ffffff',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        position: 'relative'
                      }}
                    >
                      {item.badge && (
                        <span style={{
                          position: 'absolute',
                          top: 4,
                          right: 6,
                          fontSize: '0.6rem',
                          background: '#ff9f00',
                          color: '#fff',
                          fontWeight: 800,
                          padding: '0.1rem 0.35rem',
                          borderRadius: '3px'
                        }}>
                          {item.badge}
                        </span>
                      )}
                      <div style={{ fontSize: '0.82rem', fontWeight: 800, color: isSelected ? '#1e40af' : '#1e293b' }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: '0.74rem', color: isSelected ? '#2563eb' : '#64748b', fontWeight: 600 }}>
                        ₹{item.rate}/g
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Weight Input in Grams + Quick Chips */}
            <div style={{ marginBottom: '1.1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>
                  Weight (in Grams):
                </label>
                <span style={{ fontSize: '0.74rem', color: '#2563eb', fontWeight: 700 }}>
                  Live Metal: {formatINR(metalValue)}
                </span>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  step="0.01"
                  min="0.1"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.85rem',
                    borderRadius: '6px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    color: '#0f172a',
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g. 8.50"
                />
                <span style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: '#94a3b8'
                }}>
                  grams
                </span>
              </div>
              {/* Quick weight buttons */}
              <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                {[2, 5, 8, 10, 15, 25, 50].map((wt) => (
                  <button
                    key={wt}
                    type="button"
                    onClick={() => setWeight(wt)}
                    style={{
                      background: Number(weight) === wt ? '#2874f0' : '#ffffff',
                      color: Number(weight) === wt ? '#ffffff' : '#475569',
                      border: '1px solid #cbd5e1',
                      borderRadius: '4px',
                      padding: '0.2rem 0.5rem',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {wt}g
                  </button>
                ))}
              </div>
            </div>

            {/* Making Charge Input with Type Toggle */}
            <div style={{ marginBottom: '1.1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>
                  Making Charges:
                </label>
                <div style={{ display: 'flex', gap: '0.2rem', background: '#e2e8f0', padding: '2px', borderRadius: '4px' }}>
                  <button
                    type="button"
                    onClick={() => setMakingChargeType('percent')}
                    style={{
                      border: 'none',
                      background: makingChargeType === 'percent' ? '#ffffff' : 'transparent',
                      color: makingChargeType === 'percent' ? '#0f172a' : '#64748b',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      padding: '0.15rem 0.45rem',
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                  >
                    Percentage (%)
                  </button>
                  <button
                    type="button"
                    onClick={() => setMakingChargeType('perGram')}
                    style={{
                      border: 'none',
                      background: makingChargeType === 'perGram' ? '#ffffff' : 'transparent',
                      color: makingChargeType === 'perGram' ? '#0f172a' : '#64748b',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      padding: '0.15rem 0.45rem',
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                  >
                    Per Gram (₹/g)
                  </button>
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={makingChargeValue}
                  onChange={(e) => setMakingChargeValue(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.85rem',
                    borderRadius: '6px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    color: '#0f172a',
                    boxSizing: 'border-box'
                  }}
                  placeholder={makingChargeType === 'percent' ? 'e.g. 6.99' : 'e.g. 450'}
                />
                <span style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: '#94a3b8'
                }}>
                  {makingChargeType === 'percent' ? '%' : '₹ / g'}
                </span>
              </div>
            </div>

            {/* Wastage & Stone Charges Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem', marginBottom: '1.1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '0.4rem' }}>
                  Wastage (%):
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={wastagePercent}
                  onChange={(e) => setWastagePercent(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.85rem',
                    borderRadius: '6px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: '#0f172a',
                    boxSizing: 'border-box'
                  }}
                  placeholder="0.0%"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '0.4rem' }}>
                  Stone / Diamond (₹):
                </label>
                <input
                  type="number"
                  step="100"
                  min="0"
                  value={stoneCharges}
                  onChange={(e) => setStoneCharges(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.85rem',
                    borderRadius: '6px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: '#0f172a',
                    boxSizing: 'border-box'
                  }}
                  placeholder="₹0"
                />
              </div>
            </div>

            {/* Special Making Discount (%) */}
            <div style={{ marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#15803d' }}>
                  ✨ Making Charge Discount (% OFF):
                </label>
                <span style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: 800 }}>
                  Save {formatINR(discountAmount)}
                </span>
              </div>
              <input
                type="number"
                step="5"
                min="0"
                max="100"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.65rem 0.85rem',
                  borderRadius: '6px',
                  border: '1.5px solid #86efac',
                  background: '#f0fdf4',
                  fontSize: '0.95rem',
                  fontWeight: 800,
                  color: '#15803d',
                  boxSizing: 'border-box'
                }}
                placeholder="e.g. 50"
              />
            </div>

            <button
              type="button"
              onClick={handleReset}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                background: 'transparent',
                border: '1px solid #cbd5e1',
                borderRadius: '4px',
                padding: '0.4rem 0.75rem',
                fontSize: '0.76rem',
                fontWeight: 600,
                color: '#64748b',
                cursor: 'pointer'
              }}
            >
              <RotateCcw size={13} />
              <span>Reset Values</span>
            </button>
          </div>

          {/* RIGHT COLUMN: Official Real-time Price Breakdown Card */}
          <div style={{
            background: '#ffffff',
            borderRadius: '8px',
            border: '2px solid #e0b046',
            boxShadow: '0 8px 24px rgba(224, 176, 70, 0.15)',
            padding: '1.4rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem', paddingBottom: '0.6rem', borderBottom: '1px solid #f1f5f9' }}>
                <div>
                  <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#b78103', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Live Price Estimate
                  </span>
                  <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#1e293b' }}>
                    {getMetalTitle()}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Rate per gram</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#2563eb' }}>
                    ₹{currentRate}/g
                  </div>
                </div>
              </div>

              {/* Breakdown List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', fontSize: '0.82rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#334155' }}>
                  <span>Metal Value ({parsedWeight}g × ₹{currentRate}):</span>
                  <span style={{ fontWeight: 700 }}>{formatINR(metalValue)}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#334155' }}>
                  <span>
                    Making Charges ({makingChargeValue}{makingChargeType === 'percent' ? '%' : '₹/g'}):
                  </span>
                  <span style={{ fontWeight: 700 }}>{formatINR(rawMakingCharge)}</span>
                </div>

                {wastageCharges > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#334155' }}>
                    <span>Wastage ({wastagePercent}%):</span>
                    <span style={{ fontWeight: 700 }}>{formatINR(wastageCharges)}</span>
                  </div>
                )}

                {parsedStoneCharges > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#334155' }}>
                    <span>Stone / Diamond Charges:</span>
                    <span style={{ fontWeight: 700 }}>{formatINR(parsedStoneCharges)}</span>
                  </div>
                )}

                {discountAmount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#16a34a' }}>
                    <span>Special Making Offer ({discountPercent}% OFF):</span>
                    <span style={{ fontWeight: 800 }}>- {formatINR(discountAmount)}</span>
                  </div>
                )}

                <div style={{
                  paddingTop: '0.5rem',
                  borderTop: '1px dashed #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: '#475569',
                  fontSize: '0.8rem'
                }}>
                  <span>Taxable Subtotal:</span>
                  <span style={{ fontWeight: 700 }}>{formatINR(taxableAmount)}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569', fontSize: '0.8rem' }}>
                  <span>Applicable GST (3% Precious Metals):</span>
                  <span style={{ fontWeight: 700 }}>{formatINR(gstAmount)}</span>
                </div>
              </div>

              {/* Total Final Price Box */}
              <div style={{
                background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                border: '1.5px solid #fde68a',
                borderRadius: '8px',
                padding: '0.9rem 1.1rem',
                marginTop: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Final Estimated Price
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#b45309' }}>
                    Inclusive of 3% GST & BIS Hallmark
                  </div>
                </div>
                <div style={{ fontSize: '1.65rem', fontWeight: 900, color: '#78350f' }}>
                  {formatINR(finalPrice)}
                </div>
              </div>
            </div>

            {/* Action Buttons: WhatsApp Share & Print Quote */}
            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1.2rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleShareWhatsApp}
                style={{
                  flex: 1,
                  minWidth: '150px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.45rem',
                  background: '#25D366',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.68rem 1rem',
                  fontSize: '0.84rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(37, 211, 102, 0.3)'
                }}
              >
                <MessageCircle size={16} />
                <span>Share on WhatsApp</span>
              </button>

              <button
                type="button"
                onClick={handlePrint}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  background: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  padding: '0.68rem 0.95rem',
                  fontSize: '0.84rem',
                  fontWeight: 600,
                  color: '#334155',
                  cursor: 'pointer'
                }}
              >
                <Printer size={15} />
                <span>Print</span>
              </button>
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 640px) {
            .jewel-calc-card {
              grid-template-columns: 1fr !important;
              padding: 1rem 0.75rem !important;
              gap: 1.25rem !important;
            }
          }
        `}</style>
      </div>
    </section>
  );
}
